import React from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Colour helpers ────────────────────────────────────────────────────────────
function signalColor(signal) {
  if (signal === 'BULLISH') return '#00ff88';
  if (signal === 'BEARISH') return '#ff4444';
  return '#ffaa00';
}
function signalBg(signal) {
  if (signal === 'BULLISH') return 'rgba(0,255,136,0.08)';
  if (signal === 'BEARISH') return 'rgba(255,68,68,0.08)';
  return 'rgba(255,170,0,0.08)';
}
function fqColor(score) {
  if (score === null || score === undefined) return '#475569';
  if (score >= 70) return '#00ff88';
  if (score >= 50) return '#ffaa00';
  if (score >= 30) return '#ff8800';
  return '#ff4444';
}
function dimColor(score, max) {
  if (score === null || score === undefined) return '#334155';
  const ratio = score / max;
  if (ratio >= 0.8) return '#00ff88';
  if (ratio >= 0.52) return '#ffaa00';
  if (ratio >= 0.24) return '#ff8800';
  return '#ff4444';
}

// ── ScoreRing ─────────────────────────────────────────────────────────────────
const ScoreRing = ({ score, signal }) => {
  const r = 36, circ = 2 * Math.PI * r;
  const fill = circ - (score / 100) * circ;
  const color = signalColor(signal);
  return (
    <svg width="100" height="100" style={{ transform: 'rotate(-90deg)' }}>
      <circle cx="50" cy="50" r={r} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={circ} strokeDashoffset={fill}
        style={{ transition: 'stroke-dashoffset 1s ease', strokeLinecap: 'round' }} />
      <text x="50" y="54" textAnchor="middle" fill={color}
        style={{ transform: 'rotate(90deg) translate(0px,-100px)', fontSize: '1.3rem', fontWeight: 900, fontFamily: 'inherit' }}>
        {score}
      </text>
    </svg>
  );
};

// ── SignalBadge ───────────────────────────────────────────────────────────────
const SignalBadge = ({ signal }) => {
  const color = signalColor(signal);
  const bg    = signalBg(signal);
  const border = signal === 'BULLISH' ? 'rgba(0,255,136,0.3)' : signal === 'BEARISH' ? 'rgba(255,68,68,0.3)' : 'rgba(255,170,0,0.3)';
  const icon   = signal === 'BULLISH' ? '▲' : signal === 'BEARISH' ? '▼' : '◆';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
      padding: '0.4rem 1rem', background: bg, border: '1px solid ' + border,
      borderRadius: '20px', fontSize: '0.85rem', fontWeight: 800, color, letterSpacing: '0.08em' }}>
      {icon} {signal}
    </span>
  );
};

// ── FQGateBanner ─────────────────────────────────────────────────────────────
// Shown only when FQ gate fires (FQ < 50). High-visibility strip explaining why
// the signal was constrained. No other retail tool shows this level of transparency.
const FQGateBanner = ({ fqScore, fqGate }) => {
  if (fqScore === null || fqScore === undefined || fqScore >= 50) return null;
  const isRedFlag = fqScore < 30;
  const color     = isRedFlag ? '#ff4444' : '#ff8800';
  const bg        = isRedFlag ? 'rgba(255,68,68,0.08)' : 'rgba(255,136,0,0.08)';
  const border    = isRedFlag ? 'rgba(255,68,68,0.35)' : 'rgba(255,136,0,0.35)';
  const icon      = isRedFlag ? '⛔' : '⚠';
  const label     = isRedFlag ? 'FUNDAMENTAL RED FLAG' : 'FUNDAMENTAL CONCERN';
  const sublabel  = isRedFlag ? 'Signal capped at BEARISH' : 'Upside capped at NEUTRAL';
  return (
    <div style={{
      width: '100%',
      borderLeft: '3px solid ' + color,
      background: bg,
      border: '1px solid ' + border,
      borderLeftWidth: '3px',
      borderRadius: '6px',
      padding: '0.45rem 0.65rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ fontSize: '0.75rem' }}>{icon}</span>
        <span style={{ fontSize: '0.68rem', fontWeight: 800, color, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          {label}
        </span>
      </div>
      <div style={{ fontSize: '0.63rem', color: '#64748b', marginTop: '0.1rem', paddingLeft: '1.1rem' }}>
        {sublabel}
      </div>
    </div>
  );
};

// ── FundamentalQuality ────────────────────────────────────────────────────────
// The "Institutional Lens" — always visible when fqScore is available.
// Shows the two-layer score breakdown at a granularity no retail tool matches:
//   • Overall FQ score + tier label
//   • 4 dimension cells in a 2×2 grid, each with micro-fill bar and score
//   • Dims without data shown as N/A with dashed border (full transparency)
//   • MQ score shown alongside FQ so the blend logic is visible
const FundamentalQuality = ({ fqScore, fqGate, fqDimensions, mqScore }) => {
  if (fqScore === null || fqScore === undefined) return null;

  const color   = fqColor(fqScore);
  const bg      = 'rgba(255,255,255,0.03)';
  const border  = 'rgba(255,255,255,0.08)';

  // Gate tier label colour
  const gateColor = fqScore >= 70 ? '#00ff88' : fqScore >= 50 ? '#ffaa00' : fqScore >= 30 ? '#ff8800' : '#ff4444';

  // 2×2 dim grid data
  const dims = [
    { key: 'A', label: 'Revenue',  score: fqDimensions?.A ?? null, max: 25, tip: 'Revenue growth YOY' },
    { key: 'B', label: 'Margin',   score: fqDimensions?.B ?? null, max: 25, tip: 'Profit margin' },
    { key: 'C', label: 'Debt',     score: fqDimensions?.C ?? null, max: 25, tip: 'Debt-to-equity ratio' },
    { key: 'D', label: 'ROE',      score: fqDimensions?.D ?? null, max: 25, tip: 'Return on equity' },
  ];

  return (
    <div style={{ width: '100%', background: bg, border: '1px solid ' + border, borderRadius: '8px', padding: '0.55rem 0.65rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>

      {/* Header: FQ label + score + gate tier + MQ side-by-side */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
          <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#475569', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            Fundamental Quality
          </span>
          <span style={{ fontSize: '0.63rem', fontWeight: 700, color: gateColor, letterSpacing: '0.05em' }}>
            {fqGate}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
          {/* FQ score */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color, lineHeight: 1 }}>{fqScore}</div>
            <div style={{ fontSize: '0.52rem', color: '#334155', fontWeight: 600 }}>FQ /100</div>
          </div>
          <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.08)' }} />
          {/* MQ score */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#64748b', lineHeight: 1 }}>{mqScore}</div>
            <div style={{ fontSize: '0.52rem', color: '#334155', fontWeight: 600 }}>MQ /100</div>
          </div>
        </div>
      </div>

      {/* FQ progress bar */}
      <div style={{ width: '100%', height: '3px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: fqScore + '%',
          background: color,
          borderRadius: '2px',
          transition: 'width 0.8s ease',
        }} />
      </div>

      {/* 2×2 Dimension grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.3rem' }}>
        {dims.map(({ key, label, score, max }) => {
          const hasData    = score !== null && score !== undefined;
          const dc         = hasData ? dimColor(score, max) : '#334155';
          const fillPct    = hasData ? (score / max) * 100 : 0;
          const cellBorder = hasData
            ? '1px solid rgba(255,255,255,0.07)'
            : '1px dashed rgba(255,255,255,0.12)';
          return (
            <div key={key} style={{
              background: 'rgba(255,255,255,0.02)',
              border: cellBorder,
              borderRadius: '5px',
              padding: '0.3rem 0.4rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '0.2rem',
            }}>
              {/* Dim letter + name */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '0.56rem', fontWeight: 800, color: '#334155', letterSpacing: '0.06em' }}>{key}</span>
                <span style={{ fontSize: '0.62rem', fontWeight: 700, color: hasData ? '#94a3b8' : '#334155' }}>{label}</span>
              </div>
              {/* Micro fill bar */}
              <div style={{ width: '100%', height: '2px', background: 'rgba(255,255,255,0.05)', borderRadius: '1px', overflow: 'hidden' }}>
                {hasData && (
                  <div style={{ height: '100%', width: fillPct + '%', background: dc, borderRadius: '1px', transition: 'width 0.8s ease' }} />
                )}
              </div>
              {/* Score */}
              <div style={{ textAlign: 'right' }}>
                {hasData
                  ? <span style={{ fontSize: '0.7rem', fontWeight: 900, color: dc }}>{score}<span style={{ fontSize: '0.52rem', color: '#475569', fontWeight: 600 }}>/{max}</span></span>
                  : <span style={{ fontSize: '0.62rem', color: '#334155', fontWeight: 600 }}>—</span>
                }
              </div>
            </div>
          );
        })}
      </div>

      {/* Blend note — shows the weighting logic */}
      <div style={{ fontSize: '0.56rem', color: '#334155', textAlign: 'center', letterSpacing: '0.04em' }}>
        Combined: FQ 40% · MQ 60%
      </div>
    </div>
  );
};

// ── AnalystTarget ─────────────────────────────────────────────────────────────
// Wall St. consensus target from AV AnalystTargetPrice.
// Direction + magnitude shown — simple, high signal-to-noise.
const AnalystTarget = ({ analystTargetPrice, analystVsCurrent }) => {
  if (!analystTargetPrice) return null;
  const isUpside   = analystVsCurrent !== null && analystVsCurrent > 0;
  const isDownside = analystVsCurrent !== null && analystVsCurrent < 0;
  const color      = isUpside ? '#00ff88' : isDownside ? '#ff4444' : '#64748b';
  const arrow      = isUpside ? '▲' : isDownside ? '▼' : '→';
  const pctStr     = analystVsCurrent !== null
    ? (analystVsCurrent > 0 ? '+' : '') + analystVsCurrent.toFixed(1) + '%'
    : '';
  return (
    <div style={{
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0.35rem 0.65rem',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.07)',
      borderRadius: '6px',
    }}>
      <span style={{ fontSize: '0.58rem', fontWeight: 700, color: '#475569', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        Wall St. Target
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 900, color }}>
          ${Number(analystTargetPrice).toFixed(2)}
        </span>
        {pctStr && (
          <span style={{ fontSize: '0.66rem', fontWeight: 700, color, display: 'flex', alignItems: 'center', gap: '0.15rem' }}>
            {arrow} {pctStr}
          </span>
        )}
      </div>
    </div>
  );
};

// ── TargetRange ───────────────────────────────────────────────────────────────
// V2/V3 model range — backward-compatible, always shown when available.
const TargetRange = ({ targetLow, targetHigh, signal }) => {
  if (!targetLow || !targetHigh) return null;
  const color = signalColor(signal);
  const bg    = signalBg(signal);
  return (
    <div style={{
      width: '100%',
      padding: '0.4rem 0.6rem',
      background: bg,
      border: '1px solid ' + color + '33',
      borderRadius: '6px',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: '0.6rem', color: '#475569', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.15rem' }}>
        Model Range (Algorithmic)
      </div>
      <div style={{ fontSize: '0.82rem', fontWeight: 800, color, letterSpacing: '0.03em' }}>
        ${Number(targetLow).toFixed(2)} — ${Number(targetHigh).toFixed(2)}
      </div>
    </div>
  );
};

// ── HeroSignal ────────────────────────────────────────────────────────────────
export default function HeroSignal({
  score, signal, handleShare, shareLoading, shareCopied,
  isLoggedIn, token, ticker, inWatchlist, setInWatchlist, wlLoading, setWlLoading,
  // V2: target price range
  targetLow, targetHigh,
  // S27-ALGO-V3: Fundamental Quality layer
  fqScore, fqGate, fqDimensions, mqScore,
  // S27-ALGO-V3: Analyst target
  analystTargetPrice, analystVsCurrent,
}) {
  return (
    <div style={{ flex:'1 1 0', padding:'1.4rem 1.5rem', display:'flex',
      flexDirection:'column', alignItems:'center', justifyContent:'center',
      gap:'0.5rem', minWidth:0 }}>

      <div style={{ fontSize:'0.68rem', fontWeight:700, letterSpacing:'0.1em',
        color:'#475569', textTransform:'uppercase' }}>AI Signal</div>

      <ScoreRing score={score} signal={signal} />
      <SignalBadge signal={signal} />

      {/* FQ gate banner — shown first when gate fires, highest visual priority */}
      <FQGateBanner fqScore={fqScore} fqGate={fqGate} />

      {/* Institutional Lens: FQ breakdown + MQ score */}
      <FundamentalQuality
        fqScore={fqScore}
        fqGate={fqGate}
        fqDimensions={fqDimensions}
        mqScore={mqScore}
      />

      {/* Wall St. analyst consensus target */}
      <AnalystTarget
        analystTargetPrice={analystTargetPrice}
        analystVsCurrent={analystVsCurrent}
      />

      {/* Model algorithmic range */}
      <TargetRange targetLow={targetLow} targetHigh={targetHigh} signal={signal} />

      <span style={{ fontSize:'0.66rem', color:'#475569', textAlign:'center', lineHeight:1.4 }}>
        🤖 Algorithmic · Not investment advice
      </span>

      <button onClick={handleShare} disabled={shareLoading} style={{
        marginTop:'0.3rem', display:'inline-flex', alignItems:'center', gap:'0.4rem',
        padding:'0.4rem 1rem', width:'100%', justifyContent:'center',
        background: shareCopied ? 'rgba(0,255,136,0.1)' : 'rgba(0,212,255,0.08)',
        border: shareCopied ? '1px solid rgba(0,255,136,0.3)' : '1px solid rgba(0,212,255,0.2)',
        borderRadius:'8px', color: shareCopied ? '#00ff88' : '#00d4ff',
        cursor: shareLoading ? 'default' : 'pointer',
        fontSize:'0.78rem', fontWeight:600, fontFamily:'inherit',
        opacity: shareLoading ? 0.6 : 1 }}>
        ↗ {shareLoading ? 'Creating…' : shareCopied ? 'Link copied!' : 'Share Analysis'}
      </button>

      {isLoggedIn && (
        <button disabled={wlLoading} onClick={async () => {
          setWlLoading(true);
          if (!token) { setWlLoading(false); return; }
          if (inWatchlist) {
            await fetch(API_URL + '/api/watchlist/' + ticker, { method:'DELETE', headers:{ Authorization:'Bearer ' + token } });
            setInWatchlist(false);
          } else {
            const res = await fetch(API_URL + '/api/watchlist/' + ticker, { method:'POST', headers:{ Authorization:'Bearer ' + token } });
            const d = await res.json();
            if (d.success) setInWatchlist(true);
          }
          setWlLoading(false);
        }} style={{
          padding:'0.35rem 0.9rem', width:'100%', textAlign:'center',
          background: inWatchlist ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.05)',
          border:'1px solid ' + (inWatchlist ? 'rgba(0,212,255,0.4)' : 'rgba(255,255,255,0.12)'),
          borderRadius:'20px', color: inWatchlist ? '#00d4ff' : '#64748b',
          fontSize:'0.75rem', fontWeight:700, cursor:'pointer',
          fontFamily:'inherit', transition:'all 0.15s' }}>
          {wlLoading ? '…' : inWatchlist ? '⭐ Saved' : '+ Watchlist'}
        </button>
      )}
    </div>
  );
}
